/**
 * src/pages/marketing/RenFestShop.jsx
 * ═══════════════════════════════════════════════════════════════════════════
 * YE OLD DISPENSARY — the browsing room of the RenFest booth.
 * (Renamed from "The Apothecary Shoppe" on 2026-09-02, Joel's call. The file,
 * the route and the `shoppe` API namespace keep the old word on purpose: a
 * rename that chases every identifier is a diff nobody can review three days
 * before opening.)
 *
 * A themed catalogue, not a checkout. There is no payment path here on purpose:
 * the booth has a counter and a POS, and the job of this screen is to let a
 * guest browse while their portrait is being painted, then walk up with a list.
 * See the "Show the merchant" panel at the end.
 *
 * ⚠️ IT NEVER NAVIGATES AWAY. Same rule as the selfie room, for the same
 * reason: a phone that leaves this page loses the generation poll and the guest
 * loses their portrait. No external links, no new tabs. If a link to the real
 * webstore is ever added, it must be gated behind "your portrait is saved".
 *
 * ⚠️ ARTWORK IS DRAWN, NOT PHOTOGRAPHED. Every vessel below is an inline SVG.
 * A festival runs on hotspots (decisions doc D-06) and a grid of product photos
 * is the single heaviest thing we could put on that connection. Drawn vessels
 * are a few hundred bytes each, render instantly, and hold the carved-wood
 * theme far better than a white-background pack shot would.
 *
 * ── ⚠️ THE CATALOGUE IS JURISDICTION-FILTERED. READ BEFORE EDITING. ────────
 * KC Renaissance Festival is in BONNER SPRINGS, KANSAS. Kansas scheduled
 * 7-hydroxymitragynine and mitragynine as Schedule I effective 2026-07-01
 * (HB 2365 / SB 430), and speciociliatine is captured as a diastereomer.
 *
 * Verified against `compliance_state_status` for KS on 2026-08-06. The
 * following are ILLEGAL in Kansas and MUST NOT appear in this catalogue, even
 * though they are among the highest-volume products in the company:
 *
 *   · 7-OH Chewable Tablets (White Vein)        — 7_oh, Schedule I 2026-07-01
 *   · 7-OH + Pseudoindoxyl Combo (Green Vein)   — contains 7-OH
 *   · Specio+ Tablets                           — speciociliatine
 *   · Rooted Kava + Alkaloid Shots              — alkaloid content, excluded
 *                                                 pending a formulation check
 *   · Any Delta-9 or HHC FLOWER, RAW MATERIAL or VAPE — form bans, K.S.A. 2-3908
 *
 * THCa flower and pre-rolls ARE legal in Kansas (delta-9-only basis) and are
 * included. Delta-8, Delta-9 edibles/drinks and CBN orals are `restricted`, not
 * illegal — per the compliance policy in CLAUDE.md, `restricted` is reference
 * data and not a blocker, so they stay.
 *
 * If this component is ever reused for an event in another state, this list is
 * WRONG and has to be re-derived from `compliance_state_status` for that state.
 * A hardcoded catalogue that silently outlives its jurisdiction is exactly the
 * failure the breach monitor exists to catch.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, Minus, ScrollText, X } from 'lucide-react';
import { GOLD, buzz } from '../../components/features/renfest/boothChrome';
import api from '../../lib/api';
import { SECTIONS, POS_ID, TENANT_SLUG, EVENT_SLUG } from '../../lib/shoppeCatalogue';

/* ─────────────────────────────────────────────────── drawn vessels ─── */

/**
 * One inline SVG per vessel shape, tinted per product. `hue` is the glass or
 * contents colour; everything else is shared so the shelf reads as a set.
 */
function Vessel({ kind, hue, hue2 }) {
  const a = hue, b = hue2 || hue;
  const id = `${kind}-${a}-${b}`.replace(/[^a-z0-9-]/gi, '');
  const glass = (
    <defs>
      <linearGradient id={`g-${id}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={a} stopOpacity="0.95" />
        <stop offset="55%" stopColor={b} stopOpacity="0.85" />
        <stop offset="100%" stopColor="#1b1208" stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id={`s-${id}`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
        <stop offset="30%" stopColor="#ffffff" stopOpacity="0.05" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>
    </defs>
  );
  const cork = <rect x="40" y="8" width="20" height="12" rx="3" fill="#8a5c2e" stroke="#4a2f14" strokeWidth="1.5" />;
  const shine = <rect x="30" y="26" width="10" height="58" rx="5" fill={`url(#s-${id})`} />;

  const shapes = {
    bottle: (
      <>
        {cork}
        <rect x="43" y="18" width="14" height="14" fill={`url(#g-${id})`} stroke="#2a1a0a" strokeWidth="2" />
        <path d="M28 34 q0-8 15-8 h14 q15 0 15 8 v46 q0 10-12 10 h-20 q-12 0-12-10 z"
              fill={`url(#g-${id})`} stroke="#2a1a0a" strokeWidth="2.5" />
        {shine}
        <rect x="34" y="56" width="32" height="20" rx="3" fill="#efe0bd" opacity=".92" stroke="#8a6a34" />
      </>
    ),
    jar: (
      <>
        <rect x="26" y="14" width="48" height="12" rx="4" fill="#8a5c2e" stroke="#4a2f14" strokeWidth="1.5" />
        <path d="M28 26 h44 v50 q0 12-14 12 h-16 q-14 0-14-12 z"
              fill={`url(#g-${id})`} stroke="#2a1a0a" strokeWidth="2.5" />
        {shine}
        <circle cx="44" cy="52" r="5" fill="#fff" opacity=".2" />
        <circle cx="58" cy="62" r="4" fill="#fff" opacity=".16" />
        <rect x="33" y="66" width="34" height="16" rx="3" fill="#efe0bd" opacity=".92" stroke="#8a6a34" />
      </>
    ),
    flask: (
      <>
        {cork}
        <path d="M44 18 h12 v20 l18 32 q6 12-7 18 h-34 q-13-6-7-18 l18-32 z"
              fill={`url(#g-${id})`} stroke="#2a1a0a" strokeWidth="2.5" />
        <path d="M34 66 h32 l6 10 q3 6-5 8 h-34 q-8-2-5-8 z" fill={b} opacity=".65" />
        {/* ⚠️ NOT THE SHARED `shine`, AND THIS IS WHY THE DRAUGHTS LOOKED BROKEN.
            `shine` is a rect at x 30-40, y 26-84 — sized for the bottle and the
            jar, whose glass fills that whole box. The flask is a CONE: above
            y≈66 its only glass is the narrow neck at x 44-56, so the shared
            highlight hung in mid-air beside the vessel and read as a white box
            floating on the wood. A highlight has to follow the silhouette it is
            highlighting, so this one runs down the neck and along the left
            slope. */}
        <rect x="46" y="21" width="3.4" height="17" rx="1.7" fill={`url(#s-${id})`} />
        <path d="M46.6 41.5 L31.6 68.5 L35.1 70.4 L50.1 43.4 Z" fill={`url(#s-${id})`} />
        <circle cx="42" cy="74" r="3" fill="#fff" opacity=".35" />
        <circle cx="55" cy="70" r="2" fill="#fff" opacity=".3" />
      </>
    ),
    tin: (
      <>
        <ellipse cx="50" cy="40" rx="30" ry="10" fill={a} stroke="#2a1a0a" strokeWidth="2.5" />
        <path d="M20 40 v22 q0 10 30 10 t30-10 v-22" fill={`url(#g-${id})`} stroke="#2a1a0a" strokeWidth="2.5" />
        <ellipse cx="50" cy="40" rx="20" ry="6" fill="#fff" opacity=".14" />
        <rect x="32" y="50" width="36" height="14" rx="3" fill="#efe0bd" opacity=".9" stroke="#8a6a34" />
      </>
    ),
    pouch: (
      <>
        <path d="M30 30 h40 l6 44 q1 12-12 12 h-28 q-13 0-12-12 z"
              fill={`url(#g-${id})`} stroke="#2a1a0a" strokeWidth="2.5" />
        <path d="M30 30 q20 10 40 0" fill="none" stroke="#2a1a0a" strokeWidth="2.5" />
        <path d="M34 24 q16 8 32 0" fill="none" stroke="#8a5c2e" strokeWidth="4" strokeLinecap="round" />
        {/* Same problem as the flask, four pixels of it: the pouch starts at
            y=30 and the shared `shine` starts at y=26, leaving a small pale nub
            above the seam. Inset to sit inside the pouch. */}
        <rect x="34" y="34" width="9" height="44" rx="4.5" fill={`url(#s-${id})`} />
        <rect x="36" y="56" width="28" height="18" rx="3" fill="#efe0bd" opacity=".9" stroke="#8a6a34" />
      </>
    ),
    bundle: (
      <>
        <path d="M50 12 l16 20 -8 4 10 16 -12 2 8 18 -14 4 -14-4 8-18 -12-2 10-16 -8-4 z"
              fill={`url(#g-${id})`} stroke="#1e2a12" strokeWidth="2" />
        <rect x="34" y="72" width="32" height="10" rx="4" fill="#8a5c2e" stroke="#4a2f14" strokeWidth="1.5" />
        <path d="M50 12 v70" stroke="#1e2a12" strokeWidth="1.4" opacity=".55" />
      </>
    ),
  };

  return (
    <svg viewBox="0 0 100 100" className="w-full h-full" role="img" aria-hidden="true">
      {glass}
      {shapes[kind] || shapes.bottle}
    </svg>
  );
}

/* ────────────────────────────────────────────────────── catalogue ─── */


/* How long a photograph has to arrive before the guest has already moved on.
   Not tuned against a spec — tuned against the fact that the vessel is ALREADY
   on screen, so a photo landing after this is a flicker on something the guest
   has finished looking at. */
const PHOTO_BUDGET_MS = 1500;

/* ⚠️ 8, AND THE NUMBER WAS MEASURED, NOT PICKED. Swept 4 / 8 / 14 / 26 against
   the real 737 images on 2026-09-02. At 4 a white halo survives around the
   product. At 14 the fill starts leaking through the WHITE PACKAGING of the
   Nano D8 pouch and eating holes in it; at 26 that pouch is destroyed. 8 keys
   every sample cleanly, pouches included. Their background is exactly
   #FFFFFF; the tolerance exists for JPEG noise, not for latitude. */
const KEY_TOL = 8;

/* How light a pixel ON THE BOUNDARY has to be before it is treated as leftover
   halo rather than product. Deliberately far looser than KEY_TOL, because it
   can only ever apply to pixels already touching transparency. */
const FEATHER_TOL = 95;

/* Through Netlify's Image CDN at roughly the tile's real display size. The host
   must be in `[images] remote_images` in netlify.toml or this 400s — see the
   comment there, because the failure looks exactly like a broken image.
   ⚠️ It also serves from OUR origin, which is what keeps the canvas below
   untainted and readable. A direct S3 URL would taint it and `getImageData`
   would throw. */
const cdnUrl = (src) => `/.netlify/images?url=${encodeURIComponent(src)}&w=400&fit=contain&fm=avif&q=72`;

/**
 * ⚠️ REMOVE THE BACKGROUND, DO NOT DECORATE IT. Joel, 2026-09-02, on the
 * parchment disc that used to live here: *"These pictures still have a weird
 * circle white background behind them, and the SVGs that we made, those don't
 * have any weird background. It just needs to have the same background."* He is
 * right, and the disc was a workaround for a problem that can simply be solved.
 *
 * These are catalogue pack shots on #FFFFFF. A CSS blend cannot remove that
 * white without also removing white PRODUCTS — most of this catalogue is white
 * bottles and white pouches — so every previous attempt either left a white box
 * or dissolved the merchandise. Keying the background needs to know which white
 * is background, and that is a question about CONNECTEDNESS, not colour.
 *
 * So: flood-fill inward from the border. Only white the fill can REACH from
 * outside is background. A white bottle in the middle is unreachable without
 * crossing the product, so it survives untouched — which is the whole reason
 * this works where `mix-blend-mode` and luminance keys do not.
 *
 * ⚠️ Verified on the real images before shipping, not after: bottles, jars and
 * both white pouches key clean at KEY_TOL, and the result sits on the shelf
 * exactly like the drawn vessels do.
 *
 * ⚠️ IMAGES THAT ALREADY CARRY ALPHA ARE LEFT ALONE, for free. Their border
 * pixels are transparent rather than white, so they are never seeded and the
 * fill does nothing. Roughly one in six of the 737 images is already cut out
 * (CBNight, for one), and our Image CDN preserves that alpha — checked.
 */
function keyBackground(ctx, w, h) {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const seen = new Uint8Array(w * h);
  const stack = [];
  const floor = 255 - KEY_TOL;

  const push = (x, y) => {
    const p = y * w + x;
    if (seen[p]) return;
    const i = p * 4;
    /* Already transparent is not background to clear — it is a cut-out image
       doing our job for us, and seeding from it would be meaningless. */
    if (d[i + 3] <= 8) return;
    if (d[i] < floor || d[i + 1] < floor || d[i + 2] < floor) return;
    seen[p] = 1;
    stack.push(p);
  };

  for (let x = 0; x < w; x += 1) { push(x, 0); push(x, h - 1); }
  for (let y = 0; y < h; y += 1) { push(0, y); push(w - 1, y); }

  let cleared = 0;
  while (stack.length) {
    const p = stack.pop();
    d[p * 4 + 3] = 0;
    cleared += 1;
    const x = p % w;
    const y = (p - x) / w;
    if (x > 0) push(x - 1, y);
    if (x < w - 1) push(x + 1, y);
    if (y > 0) push(x, y - 1);
    if (y < h - 1) push(x, y + 1);
  }

  /* ⚠️ THE HALO IS NOT THE BACKGROUND AND THE FLOOD CANNOT REACH IT. Between
     pure white and the product sits a ring of anti-aliased pixels — light, but
     not within KEY_TOL — so the fill stops at them and leaves a pale fringe
     that reads as a crown drawn around the product. Fade only pixels that
     TOUCH transparency, a few rings deep: the further from white, the more
     alpha survives. It cannot travel into the product because each pass needs a
     transparent neighbour to work from. */
  for (let pass = 0; pass < 3; pass += 1) {
    const edge = [];
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const p = y * w + x;
        const i = p * 4;
        if (d[i + 3] === 0) continue;
        const touches = (x > 0 && d[(p - 1) * 4 + 3] === 0)
          || (x < w - 1 && d[(p + 1) * 4 + 3] === 0)
          || (y > 0 && d[(p - w) * 4 + 3] === 0)
          || (y < h - 1 && d[(p + w) * 4 + 3] === 0);
        if (!touches) continue;
        const lo = Math.min(d[i], d[i + 1], d[i + 2]);
        if (lo >= 255 - FEATHER_TOL) {
          edge.push([i, Math.round(d[i + 3] * Math.max(0, (255 - lo) / FEATHER_TOL))]);
        }
      }
    }
    for (let k = 0; k < edge.length; k += 1) d[edge[k][0] + 3] = edge[k][1];
  }

  ctx.putImageData(img, 0, 0);
  return cleared / (w * h);
}

/**
 * ⚠️ THE PHOTOGRAPH IS A BONUS AND MUST NEVER BE A DEPENDENCY. The vessel
 * underneath is inline SVG in the bundle: it paints instantly, costs nothing
 * over the wire, and is what a guest on a bad hotspot actually sees. This fades
 * a real product photo in on top ONLY if it turns up quickly enough to be worth
 * looking at. Slow, failed or blocked and nothing happens at all — no spinner,
 * no broken-image icon, no layout shift, because the tile was never waiting.
 *
 * ⚠️ IT MEASURES. IT DOES NOT SNIFF. The obvious implementation reads
 * `navigator.connection.effectiveType` and decides up front — and that property
 * does not exist on iOS, where roughly half of festival guests are, so it would
 * report "fast" for half the crowd on any connection at all. Timing the actual
 * load is the only thing true on every phone. `saveData` is different and IS
 * honoured: it is a stated preference rather than a guess.
 *
 * ⚠️ AND IT LOADS THROUGH `new Image()`, NOT AN `<img loading="lazy">` TAG.
 * Lazy loading never fired for these at all: `complete: false`,
 * `naturalWidth: 0`, on tiles well inside the viewport, while fetching the same
 * URL by hand returned 200. The subtree is mounted by a view switch and starts
 * hidden, and Chrome's heuristic simply never decides they are worth loading.
 * It was never needed either — the Shoppe renders ONE section at a time, so at
 * most eight of these exist at once. Loading in script also sidesteps the other
 * trap: a cached image can finish before React attaches `onLoad`, so the event
 * fires into nothing and the photo never appears on any visit after the first.
 */
function Photo({ src, mode, onReveal }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    let dead = false;
    const t0 = performance.now();
    let triedRaw = false;
    const img = new Image();

    img.onload = () => {
      if (dead) return;
      const c = ref.current;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!c || !w || !h) return;
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0);
      /* ⚠️ If the key throws — a tainted canvas, a browser without
         getImageData — the photo simply keeps its background. Worse looking,
         still a photograph, never a blank tile. */
      try { keyBackground(ctx, w, h); } catch { /* keep the photo as it came */ }
      if (mode === 'on' || performance.now() - t0 <= PHOTO_BUDGET_MS) {
        setShown(true);
        onReveal();
      }
    };

    /* The Image CDN refuses some of their files — their bucket serves most of
       these as `application/octet-stream` rather than an image type. Fall back
       to the bucket once. ⚠️ The clock is NOT restarted: the guest has been
       waiting since the first attempt, and their patience is what is being
       measured. ⚠️ A raw S3 URL taints the canvas, so the key will throw and be
       swallowed above — that is the intended degradation, not a bug. */
    img.onerror = () => {
      if (dead || triedRaw) return;
      triedRaw = true;
      img.src = src;
    };

    img.src = cdnUrl(src);
    return () => { dead = true; };
  }, [src, mode, onReveal]);

  return (
    <span
      aria-hidden="true"
      style={{
        /* ⚠️ NO PLATE, NO DISC, NO BLEND MODE. The keyed photo sits on the
           shelf exactly as the drawn vessels do, because that is the whole
           point: one background, and it is the wood. */
        position: 'absolute', inset: '8%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: shown ? 1 : 0,
        transition: 'opacity .35s ease',
        pointerEvents: 'none',
      }}
    >
      {/* Intrinsic size comes from the canvas attributes, so capping both axes
          preserves the aspect ratio without `object-fit` — a bottle squashed to
          fill a square is a product a guest cannot recognise at a glance. */}
      <canvas ref={ref} style={{ maxWidth: '100%', maxHeight: '100%' }} />
    </span>
  );
}

const money = (n) => `$${n.toFixed(2)}`;

/* ⚠️ SOME SHELF ITEMS HAVE NO SINGLE PRICE, AND PRETENDING THEY DO IS THE BUG
   THIS EXISTS TO FIX. THCa Flower rings between $24.99 and $34.99 depending on
   which strain the tent actually has; showing one number means a guest is told
   a price the counter may not charge. `p2` is the upper bound when there is
   one. The BASKET still totals on `p`, the low end, deliberately: a running
   total that guesses high reads as a quote, and this list is a conversation
   starter for a staffer, not an invoice. */
const priceLabel = (it) => (it.p2 ? `${money(it.p)} – ${money(it.p2)}` : money(it.p));


/* ═══════════════════════════════════════════════════════ component ═══ */

export default function RenFestShop() {
  const [section, setSection] = useState('confections');
  const [basket, setBasket] = useState({});     // key -> qty

  /* ⚠️ THE COUNTER-ORDER SWITCH, AND IT FAILS TO THE OLD BEHAVIOUR ON PURPOSE.
     `hold` stays false unless the server says exactly `enabled: true`. A throw,
     a 500, a slow tent connection, a booth token that has not minted yet: every
     one of those leaves the guest with "Show the merchant", which is a working
     product and was the whole Shoppe until today. Joel, 2026-09-04: *"make sure
     a path that if this isn't working ... we can turn it on and off and allow
     the old way to persist."* This is that path. */
  const [hold, setHold] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState('');
  const [showList, setShowList] = useState(false);
  const [live, setLive] = useState(null);       // Map(pos id -> feed row) | null
  const [off, setOff] = useState(null);         // Set(pos id switched off) | null
  const [photos, setPhotos] = useState('auto'); // auto | on | off, from the event
  /* ⚠️ WHICH TILES HAVE A REAL PHOTOGRAPH ON THEM, so the drawn vessel can get
     out of the way. Joel, 2026-09-02, looking at the live shelf: *"we still
     have the stupid product behind it the whole time."* He was right — the
     photo has a transparent background now, so the jar underneath was showing
     around and through every single one, and its lid was the ragged edge that
     looked like a crown drawn over the bottle. The vessel is a PLACEHOLDER
     until the photograph lands; the moment one does, it is in the way. */
  const [shot, setShot] = useState(() => new Set());


  /**
   * ⚠️ THE FEED SUPPLIES PRICE AND AVAILABILITY. IT NEVER SUPPLIES THE
   * CATALOGUE. SECTIONS above is the jurisdiction-filtered curation and stays
   * the only thing a guest can see; this call decides, for each curated item,
   * what it costs today and whether the till can ring it at all. An item the
   * register does not carry disappears rather than being offered and refused at
   * the counter. Nothing the feed returns can ever ADD a product here — which is
   * what keeps the Kansas-scheduled SKUs that live on that terminal (7-OH +
   * Pseudoindoxyl, Rooted Kava + Alkaloid Shots) off a guest's phone.
   *
   * ⚠️ AND IT NEVER FAILS THE GUEST. No live answer, an empty one, or a thrown
   * error all leave `live` null, and the Shoppe runs on its built-in prices
   * exactly as it did before. A festival hotspot must not be able to empty the
   * shelves.
   */
  useEffect(() => {
    let alive = true;
    api.shoppe.products(POS_ID)
      .then((r) => {
        const rows = Array.isArray(r?.products) ? r.products : [];
        if (alive && rows.length) setLive(new Map(rows.map((row) => [row.id, row])));
      })
      .catch(() => { /* degraded, not broken - see above */ });
    return () => { alive = false; };
  }, []);

  /**
   * ⚠️ THE THIRD QUESTION, AND THE ONLY ONE A HUMAN ANSWERS. The catalogue says
   * what we may offer; the feed says what it costs and whether the till can ring
   * it; this says what is ACTUALLY ON THE TABLE TODAY. Staff set it from the
   * console's Shelf panel and it takes effect within about fifteen seconds,
   * because the reason it exists is that somebody has noticed a problem.
   *
   * ⚠️ IT FAILS OPEN, ON PURPOSE. No answer leaves `off` null and every curated
   * item stays visible. A product shown that the tent has run out of costs a
   * guest one question at the counter; an empty Shoppe looks broken to everyone
   * at once. And this switch cannot make anything LEGAL to offer — that is the
   * curated catalogue, compiled into this bundle, which no network failure can
   * widen. Merchandising may fail soft; compliance never gets the chance to.
   */
  useEffect(() => {
    let alive = true;
    api.shoppe.shelf(TENANT_SLUG, EVENT_SLUG)
      .then((r) => {
        const ids = Array.isArray(r?.disabled) ? r.disabled : [];
        if (!alive) return;
        setOff(new Set(ids));
        if (['auto', 'on', 'off'].includes(r?.photos)) setPhotos(r.photos);
      })
      .catch(() => { /* open shelf - see above */ });
    return () => { alive = false; };
  }, []);

  /* Curated item -> what the register says about it. `pos: null` means we have
     not matched it to a SKU on this terminal, so it can never be priced or rung
     here and is dropped the moment we have live data to be sure with. */
  const flat = useMemo(() => {
    const all = SECTIONS.flatMap((s) => s.items.map((i) => ({ ...i, key: `${s.k}:${i.n}`, section: s.n })));
    /* Switched off by staff is the one rule that applies even with no live
       prices — it is a human decision and does not depend on the register
       answering. */
    const shown = off ? all.filter((i) => !(i.pos && off.has(i.pos))) : all;

    /**
     * ⚠️ A VARIANT REPLACES ITS BASE ONLY ONCE THE BASE'S REPLACEMENT IS REALLY
     * ON THE SHELF. `supersededBy` names another catalogue item; this drops the
     * older one, but ONLY if that replacement survived pricing just above.
     *
     * The alternative — deleting the base outright — is what makes a section go
     * dark. On 2026-09-03 the 10 Year tin was switched on at terminal 738 and
     * not 737, so deleting the plain 250mg would have left Salves holding only
     * product 948, which is itself absent from the feed, and the tab would have
     * rendered empty again the day before opening. A swap that can empty a
     * shelf is not a swap, it is a race.
     */
    const supersede = (list) => {
      const there = new Set(list.map((i) => i.n));
      return list.filter((i) => !(i.supersededBy && there.has(i.supersededBy)));
    };

    if (!live) return supersede(shown);          // built-in prices, show everything
    const priced = shown.reduce((out, i) => {
      const row = i.pos ? live.get(i.pos) : null;
      if (!row) return out;
      /* ⚠️ A VARIANT IS PRICED BY ITS OWN OPTION, NEVER BY THE BASE PRODUCT.
         `opt` names one option value on that SKU. The 10 Year Anniversary tin
         and the plain 250mg cream are BOTH product 151 at different prices, so
         reading `row.cents` for the variant would put $59.99 on a guest's phone
         for a $41.99 tin.

         ⚠️ AND A MISSING OPTION MEANS DROP IT, NOT FALL BACK. pos-products.js
         only publishes options the register can actually ring (`pos_enabled`
         1 and `disabled` 0), so an option absent here is one the till would
         refuse. Dropping it is the same promise the base product already
         keeps: nothing is offered that cannot be sold. */
      if (i.opt) {
        const v = (row.options || []).find((o) => o.id === i.opt);
        if (!v) return out;
        out.push({ ...i, p: v.cents / 100, img: row.image || null, priced: true });
        return out;
      }
      out.push({ ...i, p: row.cents / 100, img: row.image || null, priced: true });
      return out;
    }, []);
    return supersede(priced);
  }, [live, off]);

  /**
   * A SECTION WITH NOTHING IN IT IS NOT A SECTION. Tabs come from what survived
   * pricing and the staff switch, not from the curated list, so a guest is never
   * handed a chip that opens onto bare wood.
   *
   * This is load-bearing now that Sundries is a single product (Blue Lotus,
   * #1115) and the 10 Year tin is option-gated: one `pos_enabled` switch off at
   * terminal 737 and the old code left the tab, the heading and the blurb on
   * screen above an empty grid. Curation is compiled in, availability is not.
   *
   * ⚠️ Falling back to `SECTIONS[0]` would reintroduce the same hole, since the
   * first section can empty too. The fallback is the first section that has
   * something to show, and only if EVERY section is empty does the guest see the
   * curated first one, which is the "feed is down" case where `flat` is the
   * built-in list and nothing is empty anyway.
   */
  const tabs = useMemo(() => {
    const has = new Set(flat.map((i) => i.section));
    return SECTIONS.filter((s) => has.has(s.n));
  }, [flat]);

  const active = tabs.find((s) => s.k === section) || tabs[0]
    || SECTIONS.find((s) => s.k === section) || SECTIONS[0];

  /* The shelf for the section on screen, after availability has been applied. */
  const shelf = useMemo(() => flat.filter((i) => i.section === active.n), [flat, active]);

  useEffect(() => {
    let alive = true;
    api.renfestSelfie.shoppeHoldEnabled()
      .then((r) => { if (alive && r?.enabled === true) setHold(true); })
      .catch(() => { /* silent, and the guest keeps the old list */ });
    return () => { alive = false; };
  }, []);

  const lines = useMemo(
    () => flat.filter((i) => basket[i.key]).map((i) => ({ ...i, qty: basket[i.key] })),
    [flat, basket],
  );
  const total = lines.reduce((n, l) => n + l.p * l.qty, 0);
  const count = lines.reduce((n, l) => n + l.qty, 0);

  const bump = (key, by) => {
    buzz(by > 0 ? 12 : 8);
    setBasket((b) => {
      const next = Math.max(0, (b[key] || 0) + by);
      const out = { ...b };
      if (next) out[key] = next; else delete out[key];
      return out;
    });
  };

  return (
    <div className="space-y-4">
      <div className="rf-wood p-5">
        <div className="text-center mb-4">
          <h1
            className="rf-ink"
            style={{
              fontSize: 'clamp(1.9rem, 8vw, 3rem)', lineHeight: 1, color: GOLD,
              textShadow: '0 3px 0 rgba(0,0,0,.55), 0 0 34px rgba(240,180,41,.28)',
            }}
          >
            Ye Old Dispensary
          </h1>
          <p className="rf-ink mt-1" style={{ color: 'rgba(247,228,187,.72)', fontSize: '1rem' }}>
            CBD American Shaman &middot; Bonner Springs
          </p>
        </div>

        <div className="rf-chips justify-center">
          {tabs.map((s) => (
            <button
              key={s.k}
              type="button"
              className={`rf-chip${section === s.k ? ' is-on' : ''}`}
              onClick={() => { buzz(8); setSection(s.k); }}
            >
              {s.n}
            </button>
          ))}
        </div>
      </div>

      <div className="rf-wood p-4 sm:p-5">
        <h2 className="rf-ink text-2xl" style={{ color: GOLD, textShadow: '0 2px 0 rgba(0,0,0,.55)' }}>
          {active.n}
        </h2>
        <p className="rf-ink mb-4" style={{ color: 'rgba(247,228,187,.66)' }}>{active.blurb}</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {shelf.map((it) => {
            const key = it.key;
            const qty = basket[key] || 0;
            return (
              <div key={key} className={`rf-shelf${qty ? ' is-in' : ''}`}>
                <div className="rf-vessel-bed p-3" style={{ aspectRatio: '1/1', position: 'relative' }}>
                  {/* Fades out rather than vanishing, so the swap is one motion
                      with the photo's fade in and never a flicker of bare wood. */}
                  <span
                    style={{
                      display: 'block',
                      opacity: shot.has(key) ? 0 : 1,
                      transition: 'opacity .35s ease',
                    }}
                  >
                    <Vessel kind={it.v} hue={it.c} hue2={it.c2} />
                  </span>
                  {/* ⚠️ Ordered so the vessel is what exists and the photo is what
                      arrives. `saveData` is a stated preference and is obeyed
                      without argument; everything else is decided by measurement
                      inside Photo. */}
                  {it.img && photos !== 'off' && !navigator.connection?.saveData && (
                    <Photo
                      src={it.img}
                      mode={photos}
                      onReveal={() => setShot((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))}
                    />
                  )}
                </div>

                <div className="px-2.5 pb-2.5 pt-2">
                  <p className="rf-ink leading-tight" style={{ color: '#f7e4bb', fontSize: '.98rem' }}>{it.n}</p>
                  <p className="rf-ink" style={{ color: 'rgba(247,228,187,.55)', fontSize: '.76rem' }}>{it.d}</p>

                  <div className="flex items-center gap-2 mt-2">
                    <span className="rf-tag" style={{ fontSize: '.95rem' }}>{priceLabel(it)}</span>

                    {qty === 0 ? (
                      <button
                        type="button"
                        onClick={() => bump(key, 1)}
                        className="rf-chip ml-auto"
                        style={{ minHeight: 44, padding: '.4rem .7rem' }}
                        aria-label={`Add ${it.n}`}
                      >
                        <Plus size={18} />
                      </button>
                    ) : (
                      <div className="ml-auto flex items-center gap-1">
                        <button type="button" onClick={() => bump(key, -1)} className="rf-chip" style={{ minHeight: 44, padding: '.4rem .55rem' }} aria-label="One fewer">
                          <Minus size={16} />
                        </button>
                        <span className="rf-ink" style={{ color: GOLD, minWidth: '1.4rem', textAlign: 'center', fontSize: '1.15rem' }}>{qty}</span>
                        <button type="button" onClick={() => bump(key, 1)} className="rf-chip" style={{ minHeight: 44, padding: '.4rem .55rem' }} aria-label="One more">
                          <Plus size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ⚠️ NOT A CHECKOUT. There is no payment path in this component and there
          should not be one — the booth has a counter and a POS, and a half-built
          payment flow on a stranger's phone is a liability, not a feature. This
          is a list you carry to a human. */}
      {count > 0 && (
        <div className="rf-basket rounded-2xl px-4 py-3 flex items-center gap-3">
          <div className="min-w-0">
            <p className="rf-ink text-xl leading-none" style={{ color: GOLD }}>
              {count} item{count === 1 ? '' : 's'} &middot; {money(total)}
            </p>
            <p className="text-[11px] opacity-70 mt-0.5">Take this list to the counter - no payment here</p>
          </div>
          <button
            type="button"
            onClick={() => { buzz(14); setShowList(true); }}
            className="rf-big ml-auto rounded-xl px-4 py-3 text-base flex items-center gap-2 shrink-0"
          >
            <ScrollText size={18} /> Show the merchant
          </button>
        </div>
      )}

      {showList && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3"
          style={{ background: 'rgba(10,7,4,.94)' }}
          onClick={() => setShowList(false)}
        >
          <div
            className="rf-wood w-full max-w-md p-5 max-h-[85vh] overflow-y-auto rf-scroll"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="rf-ink text-2xl" style={{ color: GOLD }}>Thy order</h3>
              <button type="button" onClick={() => setShowList(false)} className="rf-chip" style={{ minHeight: 44, padding: '.4rem .6rem' }}>
                <X size={17} />
              </button>
            </div>

            <div className="mt-3 space-y-2">
              {lines.map((l) => (
                <div key={l.key} className="flex items-baseline gap-2">
                  <span className="rf-ink" style={{ color: GOLD, minWidth: '2rem' }}>{l.qty}&times;</span>
                  <span className="rf-ink flex-1" style={{ color: '#f7e4bb' }}>{l.n}</span>
                  {/* Ranged items show the span here too, so the staffer sees
                      the same words the guest read on the shelf. */}
                  <span className="rf-ink" style={{ color: 'rgba(247,228,187,.7)' }}>
                    {l.p2 ? `${money(l.p)} – ${money(l.p2)}` : money(l.p * l.qty)}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-black/40 flex items-baseline">
              <span className="rf-ink text-xl" style={{ color: '#f7e4bb' }}>Total</span>
              <span className="rf-ink text-2xl ml-auto" style={{ color: GOLD }}>{money(total)}</span>
            </div>
            <p className="rf-ink mt-2" style={{ color: 'rgba(247,228,187,.55)', fontSize: '.82rem' }}>
              Prices before tax. Show this screen at the counter.
            </p>

            {/* ⚠️ THE SEND-AHEAD BUTTON IS ADDITIVE. It sits ABOVE the old text,
                never replaces it: if the send fails, the list the guest is
                already looking at is the answer, and it is a working one.

                ⚠️ AND IT NEVER PROMISES APPROVAL. The hold is a basket waiting
                at a till, not a cleared sale. Staff age-check 21+ items in
                person, every time (POS-INTEGRATION §2). No wording here may
                suggest otherwise. */}
            {hold && (
              <>
                <button
                  type="button"
                  disabled={sending}
                  onClick={async () => {
                    buzz(10);
                    setSending(true);
                    setSendErr('');
                    try {
                      const res = await api.renfestSelfie.shoppeOrder({
                        items: lines.map((l) => ({ product_id: l.id, quantity: l.qty })),
                      });
                      if (!res?.url) throw new Error(res?.error || 'Could not send it ahead.');
                      /* Full navigation, not window.open: a popup opened from an
                         async callback is blocked on every mobile browser. */
                      window.location.assign(res.url);
                    } catch (e) {
                      /* ⚠️ THE LIST STAYS ON SCREEN. That is the fallback, and
                         it needs no recovery step from the guest. */
                      setSendErr(e.serverError || e.message
                        || 'Could not send it ahead. Show this screen at the counter instead.');
                      setSending(false);
                    }
                  }}
                  className="rf-big w-full mt-4 rounded-2xl px-4 py-4"
                  style={{ fontSize: '1.1rem', opacity: sending ? .6 : 1 }}
                >
                  <ScrollText size={19} className="inline mr-2" />
                  {sending ? 'Sending to the counter…' : 'Send my order to the counter'}
                </button>
                <p className="rf-plain mt-2" style={{ color: 'rgba(247,228,187,.6)', fontSize: '.8rem', textAlign: 'center' }}>
                  You will get a code. Pay at booth #383 when you arrive.
                </p>
                {sendErr && (
                  <p className="rf-plain mt-2" style={{ color: '#ffb4a2', fontSize: '.85rem', textAlign: 'center' }}>
                    {sendErr}
                  </p>
                )}
              </>
            )}

            <button
              type="button"
              onClick={() => { buzz(8); setBasket({}); setShowList(false); }}
              className="rf-chip w-full mt-4 justify-center"
            >
              Clear the list
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
